import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

/**
 * 登録時 / ワークスペース作成時に使う「国推定」結果。
 * - サーバー側で MaxMind GeoLite2 + ブラウザ TZ を突合して決定する。
 * - 失敗時（callable がエラー / 未認証）は country: null / confidence: 'none' を返し、呼び出し側はそのまま続行する。
 */
export interface SignupRegionResult {
  country: string | null;
  confidence: 'high' | 'low' | 'none';
  timezone: string | null;
  language: string | null;
}

const resolveSignupRegionCallable = httpsCallable<
  { timezone?: string | null; language?: string | null },
  SignupRegionResult
>(functions, 'resolveSignupRegionFn');

/**
 * 現在のブラウザの IANA タイムゾーンと言語タグを付与して resolveSignupRegionFn を呼び、
 * 国コード（ISO 3166-1 alpha-2）と信頼度を取得する。
 *
 * - 呼び出しに失敗してもアカウント作成・WS 作成をブロックしないよう、必ず { country: null, ... } で fallback する。
 * - 認証が成立してから呼ぶ前提（Login 画面では sign-in 完了直後、CreateWorkspace では既にログイン済み）。
 */
export async function resolveSignupRegion(): Promise<SignupRegionResult> {
  let timezone: string | null = null;
  let language: string | null = null;
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    timezone = null;
  }
  try {
    language = (typeof navigator !== 'undefined' && navigator.language) || null;
  } catch {
    language = null;
  }

  try {
    const res = await resolveSignupRegionCallable({ timezone, language });
    const data = (res.data ?? {}) as Partial<SignupRegionResult>;
    return {
      country: typeof data.country === 'string' ? data.country : null,
      confidence:
        data.confidence === 'high' || data.confidence === 'low' || data.confidence === 'none'
          ? data.confidence
          : 'none',
      timezone: typeof data.timezone === 'string' ? data.timezone : timezone,
      language: typeof data.language === 'string' ? data.language : language,
    };
  } catch (e) {
    console.warn('[clientRegion] resolveSignupRegion failed (continuing without region)', e);
    return { country: null, confidence: 'none', timezone, language };
  }
}
